/**
 * Arbeitszeitmodelle je Mitarbeiter (AP-22-B).
 *
 * Versioniert über `validFrom`: Ein Wechsel von Vollzeit auf Teilzeit legt einen
 * **neuen** Satz an und lässt den alten stehen. Wer den alten überschreibt,
 * ändert rückwirkend, wie längst abgerechnete Monate zu rechnen gewesen wären.
 *
 * Eine echte Liste (je Mitarbeiter und Stichtag) und deshalb beim Listen-Standard
 * geblieben. Seit AP-36 gilt: Anlegen über einen kurzen Dialog, Bearbeiten auf der
 * Detailseite ohne Knopf. Der Export läuft über den Server und geht auch per Mail
 * (AP-10 Nachtrag 5).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@symphenia/ui-components';
import { erfuelltRecht } from '@symphenia/shared-types';
import { FilterBar } from '@/components/list/FilterBar';
import { ExportButton } from '@/components/ui/ExportButton';
import { useAuthStore } from '@/lib/auth.store';
import { bestaetige } from '@/lib/dialoge';
import { loadUiSetting } from '@/lib/ui-settings';
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types';
import {
  deleteWorkSchedule,
  fetchCollectiveAgreements,
  fetchPayrollEmployees,
  fetchWorkSchedules,
  workSchedulesExportRequest,
} from '../api/payroll.api';
import { WorkScheduleCreateDialog } from '../components/WorkScheduleCreateDialog';
import { ListTable, type ListColumn, type ListColumnState } from '../components/ListTable';
import { formatIsoDate } from '../lib/dates';
import type { PaginationMeta, PayrollEmployee, WorkSchedule } from '../types/payroll.types';

const FIXED_COLUMN: ListColumn = { key: 'employee', label: 'Mitarbeiter', sortable: true };
const ALL_COLUMNS: ListColumn[] = [
  { key: 'validFrom', label: 'Gültig ab', sortable: true },
  { key: 'weeklyHours', label: 'Wochenstunden', align: 'right', sortable: true },
  { key: 'employment', label: 'Beschäftigung', sortable: true },
  { key: 'compensation', label: 'Entlohnungsart' },
  { key: 'cycleWeeks', label: 'Zyklus', align: 'right' },
  { key: 'targetSum', label: 'Soll je Woche', align: 'right' },
  { key: 'period', label: 'Ein-/Austritt' },
  { key: 'actions', label: 'Aktionen' },
];

const SETTING_KEY = 'columnbar_layout_payroll_work_schedules';
const DEFAULT_STATE: ListColumnState = {
  columnKeys: ['validFrom', 'weeklyHours', 'employment', 'cycleWeeks', 'targetSum', 'actions'],
  columnWidths: {},
  sortKey: 'employee',
  sortDir: 'asc',
};

const EMPLOYMENT_LABEL: Record<string, string> = {
  FULL_TIME: 'Vollzeit',
  PART_TIME: 'Teilzeit',
};

function userLabel(u: PayrollEmployee): string {
  return [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ') || u.email || '—';
}

function stunden(n: number): string {
  return n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
}

export default function WorkSchedulesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const darfPflegen = erfuelltRecht('payroll.work_schedule.manage', authUser?.permissions);

  const [users, setUsers] = useState<PayrollEmployee[]>([]);
  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({});
  const [colState, setColState] = useState<ListColumnState>(() =>
    loadUiSetting<ListColumnState>(SETTING_KEY, DEFAULT_STATE),
  );

  const [rows, setRows] = useState<WorkSchedule[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [anlegen, setAnlegen] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mitarbeiter getrennt laden — ein Fehler hier darf die Liste nicht blockieren.
  useEffect(() => {
    // AP-38-D: die eigene Mitarbeiterliste der Lohnverrechnung — ohne Externe
    // und ohne `core.user.read`.
    fetchPayrollEmployees()
      .then(setUsers)
      .catch(() => setUsers([]));
  }, []);

  // AP-22 Teil 3: Namen der Entlohnungsarten für die Spalte — getrennt geladen wie die Mitarbeiter.
  const [arten, setArten] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    fetchCollectiveAgreements()
      .then((liste) => setArten(new Map(liste.map((a) => [a.id, a.name]))))
      .catch(() => setArten(new Map()));
  }, []);

  const nameOf = useCallback(
    (userId: string): string => {
      const u = users.find((x) => x.id === userId);
      return u ? userLabel(u) : '—';
    },
    [users],
  );

  const fetchData = useCallback(async (filters: Record<string, ActiveFilter>, currentPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const userFilter = filters['userId'];
      const res = await fetchWorkSchedules({
        userId: userFilter?.operator === 'is' ? userFilter.value : undefined,
        page: currentPage,
        limit: 100,
      });
      setRows(res.data);
      setMeta(res.meta);
    } catch (err) {
      setError('Die Arbeitszeitmodelle konnten nicht geladen werden.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData(filterValues, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleFiltersChange = useCallback(
    (newValues: Record<string, ActiveFilter>) => {
      setFilterValues(newValues);
      setPage(1);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => void fetchData(newValues, 1), 400);
    },
    [fetchData],
  );

  // Die Mitarbeiterliste steht erst nach dem Mount — deshalb im Rendern gebaut,
  // keine Konstante auf Modulebene.
  const availableFilterFields: FilterFieldDef[] = [
    {
      key: 'userId',
      label: 'Mitarbeiter',
      type: 'select',
      category: 'core',
      operators: ['is', 'is_not'],
      options: users.map((u) => ({ value: u.id, label: userLabel(u) })),
    },
    {
      key: 'employment',
      label: 'Beschäftigung',
      type: 'select',
      category: 'core',
      operators: ['is', 'is_not'],
      options: [
        { value: 'FULL_TIME', label: 'Vollzeit' },
        { value: 'PART_TIME', label: 'Teilzeit' },
      ],
    },
  ];

  const filteredRows = rows.filter((row) => {
    for (const [key, active] of Object.entries(filterValues)) {
      if (!active.value) continue;
      if (key === 'employment') {
        if (active.operator === 'is' && row.employment !== active.value) return false;
        if (active.operator === 'is_not' && row.employment === active.value) return false;
      }
      if (key === 'userId' && active.operator === 'is_not' && row.userId === active.value) {
        return false;
      }
    }
    return true;
  });

  const weekSum = (row: WorkSchedule): number =>
    row.days.reduce((s, d) => s + d.targetHours, 0) / Math.max(1, row.cycleWeeks);

  const displayRows = [...filteredRows].sort((a, b) => {
    const dir = colState.sortDir === 'asc' ? 1 : -1;
    if (colState.sortKey === 'validFrom') return a.validFrom.localeCompare(b.validFrom) * dir;
    if (colState.sortKey === 'weeklyHours') return (a.weeklyHours - b.weeklyHours) * dir;
    if (colState.sortKey === 'employment') return a.employment.localeCompare(b.employment) * dir;
    // Nach Mitarbeiter, innerhalb dessen der jüngste Satz zuerst.
    const cmp = nameOf(a.userId).localeCompare(nameOf(b.userId), 'de');
    return cmp !== 0 ? cmp * dir : b.validFrom.localeCompare(a.validFrom);
  });

  const handleDelete = async (row: WorkSchedule) => {
    if (
      !(await bestaetige(`Modell von ${nameOf(row.userId)} ab ${formatIsoDate(row.validFrom)} löschen?`, {
        ok: 'Löschen',
        gefaehrlich: true,
      }))
    )
      return;
    try {
      await deleteWorkSchedule(row.id);
      await fetchData(filterValues, page);
    } catch {
      setError('Löschen fehlgeschlagen.');
    }
  };

  // Was im Export über der Tabelle steht — dieselben Filter, lesbar.
  const filterInfo = [
    filterValues['userId']?.value
      ? `Mitarbeiter ${filterValues['userId'].operator === 'is_not' ? 'nicht ' : ''}${nameOf(filterValues['userId'].value)}`
      : '',
    filterValues['employment']?.value
      ? `Beschäftigung ${filterValues['employment'].operator === 'is_not' ? 'nicht ' : ''}${EMPLOYMENT_LABEL[filterValues['employment'].value] ?? ''}`
      : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const shownColumns = colState.columnKeys
    .map((k) => ALL_COLUMNS.find((c) => c.key === k))
    .filter((c): c is ListColumn => !!c && c.key !== 'actions');

  const renderCell = (row: WorkSchedule, col: ListColumn): React.ReactNode => {
    switch (col.key) {
      case 'validFrom':
        return <span className="tabular-nums">{formatIsoDate(row.validFrom)}</span>;
      case 'weeklyHours':
        return stunden(row.weeklyHours);
      case 'employment':
        return EMPLOYMENT_LABEL[row.employment] ?? row.employment;
      case 'compensation':
        return row.collectiveAgreementId ? (
          (arten.get(row.collectiveAgreementId) ?? '…')
        ) : (
          <span className="text-muted-foreground">Standard</span>
        );
      case 'cycleWeeks':
        return row.cycleWeeks > 1 ? `${row.cycleWeeks} Wochen` : '—';
      case 'targetSum':
        // Teil 7: Ein Stundenrahmen hat kein Tages-Soll — die Zelle nennt den Rahmen.
        if (row.hoursFrame && row.frameHours !== null)
          return (
            <span className="text-muted-foreground">
              Rahmen {stunden(row.frameHours)} h je {row.hoursFrame === 'MONTH' ? 'Monat' : 'Woche'}
              {row.frameOvertime ? '' : ' (Kontrolle)'}
            </span>
          );
        return stunden(weekSum(row));
      case 'period':
        return (
          <span className="text-muted-foreground">
            {row.entryDate ? formatIsoDate(row.entryDate) : '—'}
            {' bis '}
            {row.exitDate ? formatIsoDate(row.exitDate) : 'offen'}
          </span>
        );
      case 'actions':
        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => navigate(`/payroll/work-schedules/${row.id}`)}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              title={darfPflegen ? 'Öffnen und bearbeiten' : 'Öffnen'}
            >
              <Pencil size={14} />
            </button>
            {darfPflegen && (
              <button
                type="button"
                onClick={() => void handleDelete(row)}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                title="Löschen"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Arbeitszeitmodelle</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Vereinbarte Wochenstunden und Tages-Soll je Mitarbeiter — jeweils mit Gültig-ab. Ein
            Wechsel legt einen neuen Satz an, der alte bleibt stehen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            request={(format, orientation) =>
              workSchedulesExportRequest(
                {
                  userId: filterValues['userId']?.operator === 'is' ? filterValues['userId'].value : undefined,
                  format,
                  orientation,
                  filterInfo: filterInfo || undefined,
                },
                // Der Mitarbeiter ist die feste erste Spalte und gehört in jeden Export.
                [FIXED_COLUMN, ...shownColumns].map((c) => ({ key: c.key, label: c.label })),
              )
            }
            mail={{
              source: 'EXPORT_PAYROLL_WORK_SCHEDULES',
              title: t('mailExport.titles.EXPORT_PAYROLL_WORK_SCHEDULES'),
              subject: t('mailExport.titles.EXPORT_PAYROLL_WORK_SCHEDULES'),
            }}
          />
          {darfPflegen && (
            <Button size="sm" onClick={() => setAnlegen(true)}>
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Modell</span>
            </Button>
          )}
        </div>
      </div>

      <FilterBar
        availableFields={availableFilterFields}
        value={filterValues}
        onChange={handleFiltersChange}
        layoutKey="payroll_work_schedules"
        defaultFieldKeys={['userId']}
      />

      <ListTable
        allColumns={ALL_COLUMNS}
        fixedColumn={FIXED_COLUMN}
        state={colState}
        onStateChange={setColState}
        settingKey={SETTING_KEY}
        defaultState={DEFAULT_STATE}
        rows={displayRows}
        rowKey={(r) => r.id}
        renderFixedCell={(r) => (
          <Link to={`/payroll/work-schedules/${r.id}`} className="text-foreground hover:text-primary hover:underline">
            {nameOf(r.userId)}
          </Link>
        )}
        renderCell={renderCell}
        loading={loading}
        error={error}
        emptyText="Noch kein Arbeitszeitmodell hinterlegt. Ohne Modell kennt die Lohnverrechnung kein Soll."
        summaryText={
          meta && displayRows.length < meta.total
            ? `${displayRows.length} / ${meta.total} Modelle`
            : `${displayRows.length} ${displayRows.length === 1 ? 'Modell' : 'Modelle'}`
        }
      />

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between px-1 text-sm text-muted-foreground">
          <span>
            Seite {meta.page} von {meta.totalPages} ({meta.total} Einträge)
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => p - 1)}
              className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
            >
              ‹
            </button>
            <button
              type="button"
              disabled={page >= meta.totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
            >
              ›
            </button>
          </div>
        </div>
      )}

      <WorkScheduleCreateDialog
        isOpen={anlegen}
        onClose={() => setAnlegen(false)}
        // Ohne Lohnverrechnung braucht niemand ein neues Arbeitszeitmodell.
        users={users.filter((u) => !u.noPayroll)}
        onAngelegt={(ws) => {
          setAnlegen(false);
          navigate(`/payroll/work-schedules/${ws.id}`);
        }}
      />
    </div>
  );
}
